import { FC, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';

/**
 * InvitePage — /invite/:code capture route (Phase 25).
 *
 * This route mounts for unauthenticated users (hoisted above RequireAuth in App.tsx).
 * It reads the invite code from the URL param, writes it to sessionStorage, and
 * immediately redirects to '/' so the normal signup flow takes over.
 *
 * The sessionStorage write is the primary capture mechanism. callbackURL threading
 * in useAuth.signIn is the secondary mechanism for cases where sessionStorage is
 * cleared between tab navigation (e.g., user opens magic link in a different browser).
 *
 * No error state — sessionStorage write failures are swallowed (callbackURL is the fallback).
 */
const InvitePage: FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const reduced = useReducedMotion();

  useEffect(() => {
    if (code) {
      try {
        sessionStorage.setItem('referral_code', code);
      } catch {
        // sessionStorage unavailable (e.g., private mode with storage blocked).
        // Proceed — callbackURL threading is the secondary capture mechanism.
        console.error('[InvitePage] sessionStorage write failed');
      }
    }
    void navigate('/', { replace: true });
  }, [code, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface dark:bg-surface-dark">
      <div className="flex flex-col items-center gap-2">
        <Loader2
          size={24}
          className={
            reduced
              ? 'text-brand-600 dark:text-brand-400'
              : 'animate-spin text-brand-600 dark:text-brand-400'
          }
          aria-hidden="true"
        />
        <p className="text-body text-slate-500 dark:text-slate-400">
          {t('invite.preparing', 'Preparing...')}
        </p>
      </div>
    </div>
  );
};

export default InvitePage;
